<template>
  <div class="container mt-4">
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6">
        <div class="card">
          <div class="card-header">
            <h4 class="mb-0">
              <font-awesome-icon icon="user-plus" class="me-2" />
              Bereich beitreten
            </h4>
          </div>
          <div class="card-body">
            <p class="text-muted mb-4">
              Gib den Einladungscode ein, den du von einem Bereich-Administrator erhalten hast.
            </p>

              <div class="mb-3">
                <label for="invitationCode" class="form-label">Einladungscode</label>
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    id="invitationCode"
                    v-model="form.code"
                    placeholder="Einladungscode eingeben"
                    :class="{ 'is-invalid': errors.code }"
                    ref="codeInput"
                    @input="clearValidation"
                  />
                  <button 
                    class="btn btn-outline-primary"
                    type="button"
                    @click="validateCode"
                    :disabled="validating || !form.code.trim()"
                  >
                    <span v-if="validating" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <font-awesome-icon v-else icon="search" class="me-1" />
                    Prüfen
                  </button>
                </div>
                <div class="invalid-feedback" v-if="errors.code">
                  {{ errors.code }}
                </div>
              </div>

              <!-- Validierungsergebnis -->
              <div v-if="validationResult" class="mb-4">
                <div v-if="validationResult.success" class="alert alert-success">
                  <div class="mb-2">
                    <font-awesome-icon icon="check-circle" class="me-2" />
                    Bereich gefunden
                  </div>
                  <h5 class="text-center">{{ validationResult.team?.name }}</h5>
                  
                  <div v-if="validationResult.fields && validationResult.fields.length > 0" class="mt-3">
                    <div v-if="requiredFields.length > 0" class="mb-3">
                      <h6>Erforderliche Felder:</h6>
                      <ul class="mb-2">
                        <li v-for="field in requiredFields" :key="field.id">
                          {{ field.name }} ({{ getFieldTypeText(field.type) }})
                        </li>
                      </ul>
                    </div>
                    
                    <div v-if="optionalFields.length > 0">
                      <h6>Optionale Felder:</h6>
                      <ul class="mb-2">
                        <li v-for="field in optionalFields" :key="field.id">
                          {{ field.name }} ({{ getFieldTypeText(field.type) }})
                        </li>
                      </ul>
                    </div>
                    
                    <small class="text-muted">
                      Du musst alle erforderlichen Felder ausfüllen, bevor du dem Bereich beitreten kannst.
                    </small>
                  </div>
                </div>

                <div v-else class="alert alert-danger">
                  <font-awesome-icon :icon="validationResult.icon || 'exclamation-triangle'" class="me-2" />
                  {{ validationResult.message }}
                </div>
              </div>

              <!-- Allgemeine Fehlermeldung -->
              <div v-if="error" class="alert alert-danger mb-3" role="alert">
                <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                {{ error }}
              </div>

              <!-- Erfolgsmeldung -->
              <div v-if="success" class="alert alert-success mb-3" role="alert">
                <font-awesome-icon icon="check" class="me-2" />
                {{ success }}
              </div>

              <div class="d-flex justify-content-between">
               <BackButton :to="'/teams'" />
                
                <button 
                  type="submit" 
                  class="btn btn-success"
                  @click="joinWithCode"
                  :disabled="joining || !form.code.trim() || !canJoinTeam"
                >
                  <span v-if="joining" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <font-awesome-icon v-else icon="user-plus" class="me-1" />
                  Beitrittsanfrage senden
                </button>
              </div>
          </div>
        </div>

        <!-- Hilfe-Sektion -->
        <div class="card mt-4">
          <div class="card-body">
            <h6 class="card-title">
              <font-awesome-icon icon="question-circle" class="me-2" />
              Hilfe
            </h6>
            <p class="card-text mb-2">
              <strong>Wo finde ich einen Einladungscode?</strong><br>
              Bereich-Administratoren können Einladungscodes erstellen und an dich weitergeben.
            </p>
            <p class="card-text mb-0">
              <strong>Was passiert nach dem Senden der Anfrage?</strong><br>
              Ein Bereich-Administrator muss deine Anfrage genehmigen, bevor du dem Bereich beitreten kannst.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { teamService } from '@/api/teams'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { showSuccess, showError, showJoinTeamModal } from '@/services/notification'
import BackButton from '@/components/common/BackButton.vue'


const props = defineProps({
  code: {
    type: String,
    default: null
  }
})

const router = useRouter()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()


const form = reactive({
  code: ''
})


const validationResult = ref(null)

const errors = reactive({
  code: ''
})

const error = ref(null)
const success = ref(null)

const validating = ref(false)
const joining = ref(false)


const codeInput = ref(null)


const requiredFields = computed(() => {
  return validationResult.value?.fields?.filter(field => field.required) || []
})

const optionalFields = computed(() => {
  return validationResult.value?.fields?.filter(field => !field.required) || []
})

const canJoinTeam = computed(() => {
  return validationResult.value?.success === true
})


const clearValidation = () => {
  validationResult.value = null
  errors.code = ''
  error.value = null
}

const validateForm = () => {
  Object.keys(errors).forEach(key => errors[key] = '')
  let isValid = true

  if (!form.code.trim()) {
    errors.code = 'Bitte gib einen Einladungscode ein'
    isValid = false
  }

  return isValid
}

const validateCode = async () => {
  if (!validateForm()) return

  validating.value = true
  error.value = null
  validationResult.value = null

  try {
    const result = await teamService.validateInvitationCode(form.code.trim())
    validationResult.value = {
      success: true,
      team: result.team,
      fields: result.fields || []
    }
  } catch (err) {
    console.error('Fehler beim Validieren des Codes:', err)
    
    let errorMessage = 'Code konnte nicht validiert werden'
    let iconClass = 'exclamation-triangle'
    
    if (err.response) {
      const message = err.response.data?.message || ''
      
      switch (err.response.status) {
        case 404:
          errorMessage = 'Einladungscode nicht gefunden oder ungültig'
          iconClass = 'search'
          break
        case 400:

          if (message.includes('bereits Mitglied')) {
            errorMessage = 'Du bist bereits Mitglied dieses Bereichs'
            iconClass = 'user-check'
          } else if (message.includes('ausstehende Anfrage')) {
            errorMessage = 'Du hast bereits eine ausstehende Anfrage für diesen Bereich'
            iconClass = 'clock'
          } else if (message.includes('abgelaufen')) {
            errorMessage = 'Dieser Einladungscode ist abgelaufen'
            iconClass = 'clock'
          } else {
            errorMessage = message || 'Ungültiger Einladungscode'
          }
          break
        case 410:
          errorMessage = 'Dieser Einladungscode ist abgelaufen'
          iconClass = 'clock'
          break
        case 403:
          errorMessage = 'Du hast keine Berechtigung, diesen Code zu verwenden'
          iconClass = 'ban'
          break
        case 409:
          errorMessage = 'Du bist bereits Mitglied dieses Bereichs oder hast eine ausstehende Anfrage'
          iconClass = 'user-check'
          break
        default:
          errorMessage = message || errorMessage
      }
    } else if (err.message) {
      errorMessage = err.message
    }
    
    validationResult.value = {
      success: false,
      message: errorMessage,
      icon: iconClass
    }
  } finally {
    validating.value = false
  }
}

const joinWithCode = async () => {
  if (!validateForm()) return
  joining.value = true

  const result = await showJoinTeamModal(
    validationResult.value?.fields || [],
    async (responses) => {
      await sendJoinRequest(responses)
    },
    () => {

    }
  )
}

const sendJoinRequest = async (fieldResponses = {}) => {
  joining.value = true
  error.value = null
  success.value = null

  try {
    await teamService.createJoinRequest(form.code.trim(), fieldResponses)
    

    authStore.clearPendingInvitationCode()
    
  success.value = 'Beitrittsanfrage wurde erfolgreich gesendet! Du erhältst eine Benachrichtigung, sobald ein Bereich-Administrator deine Anfrage bearbeitet hat.'
    showSuccess('Beitrittsanfrage wurde erfolgreich gesendet!')
    

    await teamsStore.fetchTeams()
    router.push({ name: 'teams-list' })
  } catch (err) {
    console.error('Fehler beim Senden der Beitrittsanfrage:', err)
    
    let errorMessage = 'Beitrittsanfrage konnte nicht gesendet werden'
    
    if (err.response) {
      switch (err.response.status) {
        case 404:
          errorMessage = 'Einladungscode nicht gefunden oder ungültig'
          break
        case 400:
          errorMessage = err.response.data?.message || 'Ungültige Anfrage'
          break
        case 410:
          errorMessage = 'Dieser Einladungscode ist abgelaufen'
          break
        case 403:
          errorMessage = 'Du hast keine Berechtigung, diesem Bereich beizutreten'
          break
        case 409:
          errorMessage = 'Du bist bereits Mitglied dieses Bereichs oder hast bereits eine ausstehende Anfrage'
          break
        case 422:
          errorMessage = err.response.data?.message || 'Bitte füll alle erforderlichen Felder aus'
          break
        default:
          errorMessage = err.response.data?.message || errorMessage
      }
    } else if (err.message) {
      errorMessage = err.message
    }
    
    error.value = errorMessage
    showError(errorMessage)
  } finally {
    joining.value = false
  }
}

const goBack = () => {
  router.push({ name: 'teams-requests' })
}


const getFieldTypeText = (type) => {
  const typeMap = {
    text: 'Text',
    number: 'Zahl',
    date: 'Datum',
    boolean: 'Ja/Nein',
    textarea: 'Mehrzeiliger Text',
    checkbox: 'Checkbox',
    select: 'Auswahl'
  }
  return typeMap[type] || type
}


onMounted(async () => {

  if (props.code) {
    form.code = props.code
    await nextTick()
    await validateCode()
  } else {

    const pendingCode = authStore.getPendingInvitationCode()
    if (pendingCode) {
      form.code = pendingCode

      authStore.clearPendingInvitationCode()
      await nextTick()
      await validateCode()
    }
  }
  

  await nextTick()
  codeInput.value?.focus()
})
</script>
